import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactElement } from 'react'
import { useTranslate } from '../lib/i18n-provider'
import { listResidences, mediaFileUrl } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  THEME_BUTTON_STYLES,
  THEME_COLOR_FIELDS,
  THEME_CORNER_STYLES,
  THEME_FONT_FAMILIES,
  THEME_HEADING_STYLES,
  THEME_LAYOUTS,
  WCAG_AA_CONTRAST,
  contrastRatio,
  diffTheme,
  fetchTheme,
  isEmptyPatch,
  normalizeHexColor,
  themeErrorMessageKey,
  updateTheme,
} from '../lib/theme-client'
import type {
  ResidenceTheme,
  ThemeButtonStyle,
  ThemeChoiceField,
  ThemeColorField,
  ThemeCornerStyle,
  ThemeFailure,
  ThemeFontFamily,
  ThemeHeadingStyle,
  ThemeLayout,
} from '../lib/theme-client'

/**
 * Het scherm waarop de eigenaar zijn huisstijl instelt.
 *
 * De huisstijl is configuratie, geen code (§4.7, §4.8): dezelfde applicatie
 * presenteert zich als het merk van deze residence omdat deze tien waarden anders
 * staan. Wat hier gekozen wordt, is precies wat de gastensite leest — niet meer,
 * en vooral geen vrije opmaak.
 *
 * Drie keuzes die geen detail zijn:
 *
 *  1. **De voorbeeldweergave staat naast de velden en niet achter een knop.**
 *     Een kleur is geen getal maar een indruk; wie zijn keuze pas ziet nadat hij
 *     heeft opgeslagen en de gastensite heeft geopend, kiest blind.
 *  2. **Het contrast wordt gemeld, niet afgedwongen.** Een eigenaar mag een
 *     eigenzinnige keuze maken; hij hoort alleen niet per ongeluk een onleesbare
 *     site te bouwen. Vandaar een waarschuwing met de gemeten verhouding erbij,
 *     en een opslaanknop die gewoon blijft werken.
 *  3. **Alleen het verschil gaat mee in de PATCH.** Zie `diffTheme`: een aanvraag
 *     die alle velden herhaalt, overschrijft stilletjes wat een collega intussen
 *     heeft aangepast.
 *
 * De kleurwaarden komen in een style-attribuut van de voorbeeldweergave terecht.
 * Ze passeren daarvoor twee sloten: `fetchTheme` weigert een antwoord met een
 * kleur die niet `#rrggbb` is, en het tekstveld hieronder neemt een ingetikte
 * waarde pas over zodra `normalizeHexColor` hem herkent. Wat niet door beide komt,
 * bereikt het attribuut niet.
 */

// ---------------------------------------------------------------------------
// Vertaalsleutels
// ---------------------------------------------------------------------------
//
// Bewust uitgeschreven en niet met `dashboard.theme.color.${field}` samengesteld:
// een samengestelde sleutel is niet terug te vinden met zoeken, en dan is een
// ontbrekende vertaling pas op het scherm te ontdekken.

const COLOR_LABEL_KEYS: Readonly<Record<ThemeColorField, string>> = {
  primaryColor: 'dashboard.theme.color.primary',
  secondaryColor: 'dashboard.theme.color.secondary',
  accentColor: 'dashboard.theme.color.accent',
  textColor: 'dashboard.theme.color.text',
  backgroundColor: 'dashboard.theme.color.background',
}

const FONT_FAMILY_LABEL_KEYS: Readonly<Record<ThemeFontFamily, string>> = {
  system: 'dashboard.theme.fontFamily.system',
  serif: 'dashboard.theme.fontFamily.serif',
  humanist: 'dashboard.theme.fontFamily.humanist',
  geometric: 'dashboard.theme.fontFamily.geometric',
}

const HEADING_STYLE_LABEL_KEYS: Readonly<Record<ThemeHeadingStyle, string>> = {
  regular: 'dashboard.theme.headingStyle.regular',
  condensed: 'dashboard.theme.headingStyle.condensed',
  wide: 'dashboard.theme.headingStyle.wide',
}

const BUTTON_STYLE_LABEL_KEYS: Readonly<Record<ThemeButtonStyle, string>> = {
  rounded: 'dashboard.theme.buttonStyle.rounded',
  pill: 'dashboard.theme.buttonStyle.pill',
  square: 'dashboard.theme.buttonStyle.square',
}

const CORNER_STYLE_LABEL_KEYS: Readonly<Record<ThemeCornerStyle, string>> = {
  sharp: 'dashboard.theme.cornerStyle.sharp',
  soft: 'dashboard.theme.cornerStyle.soft',
  round: 'dashboard.theme.cornerStyle.round',
}

const LAYOUT_LABEL_KEYS: Readonly<Record<ThemeLayout, string>> = {
  premium: 'dashboard.theme.layout.premium',
  compact: 'dashboard.theme.layout.compact',
  editorial: 'dashboard.theme.layout.editorial',
}

// ---------------------------------------------------------------------------
// Toestand
// ---------------------------------------------------------------------------

type ResidencesState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly residences: readonly ResidenceOption[] }
  | { readonly phase: 'error'; readonly error: ThemeFailure }

type ThemeState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'loading' }
  /** `server` is wat de API bevestigd heeft; het verschil met de bewerking is de PATCH. */
  | { readonly phase: 'ready'; readonly server: ResidenceTheme }
  | { readonly phase: 'error'; readonly error: ThemeFailure }

/**
 * Wat er op het scherm staat: de bewerkte huisstijl plus de letterlijke tekst in
 * de hexvelden.
 *
 * Die twee zijn bewust gescheiden. `theme` bevat uitsluitend geldige kleuren —
 * dat is de belofte waarop de voorbeeldweergave leunt — terwijl `colorText`
 * bewaart wat iemand aan het intikken is, ook halverwege ("#1f2"). Zonder dat
 * tweede veld zou het invoerveld bij elke toetsaanslag terugspringen.
 */
interface Editor {
  readonly theme: ResidenceTheme
  readonly colorText: Readonly<Record<ThemeColorField, string>>
}

function toEditor(theme: ResidenceTheme): Editor {
  return {
    theme,
    colorText: {
      primaryColor: theme.primaryColor,
      secondaryColor: theme.secondaryColor,
      accentColor: theme.accentColor,
      textColor: theme.textColor,
      backgroundColor: theme.backgroundColor,
    },
  }
}

/**
 * Beperkt een waarde uit een keuzelijst tot de toegestane verzameling. De lijst
 * biedt niets anders aan, dus de terugval treedt alleen op bij een browser die
 * iets anders terugstuurt — en dan is de bestaande waarde het juiste antwoord.
 */
function pickOption<T extends string>(value: string, allowed: readonly T[], fallback: T): T {
  const known: readonly string[] = allowed
  return known.includes(value) ? (value as T) : fallback
}

// ---------------------------------------------------------------------------
// Contrast
// ---------------------------------------------------------------------------

interface ContrastCheck {
  readonly id: string
  readonly messageKey: string
  readonly ratio: number
}

/**
 * De combinaties die op de gastensite werkelijk naast elkaar komen te staan, en
 * die dus leesbaar moeten zijn. Alle drie zijn ook in de voorbeeldweergave te
 * zien, zodat de waarschuwing verwijst naar iets wat de eigenaar voor zich heeft.
 */
function contrastChecks(theme: ResidenceTheme): readonly ContrastCheck[] {
  const pairs: readonly {
    readonly id: string
    readonly messageKey: string
    readonly foreground: string
    readonly background: string
  }[] = [
    {
      id: 'textOnBackground',
      messageKey: 'dashboard.theme.contrast.textOnBackground',
      foreground: theme.textColor,
      background: theme.backgroundColor,
    },
    {
      id: 'textOnSecondary',
      messageKey: 'dashboard.theme.contrast.textOnSecondary',
      foreground: theme.textColor,
      background: theme.secondaryColor,
    },
    {
      id: 'buttonLabel',
      messageKey: 'dashboard.theme.contrast.buttonLabel',
      foreground: theme.backgroundColor,
      background: theme.primaryColor,
    },
  ]

  const checks: ContrastCheck[] = []
  for (const pair of pairs) {
    const ratio = contrastRatio(pair.foreground, pair.background)
    if (ratio === null) {
      // Kan niet: `theme` bevat alleen gevalideerde kleuren. Overslaan is hier
      // beter dan een melding met een leeg getal.
      continue
    }
    checks.push({ id: pair.id, messageKey: pair.messageKey, ratio })
  }
  return checks
}

/** Eén decimaal; "4,5:1" is de notatie waarin de WCAG-drempel bekendstaat. */
function roundRatio(ratio: number): number {
  return Math.round(ratio * 10) / 10
}

function ContrastNotice({ theme }: { readonly theme: ResidenceTheme }): ReactElement {
  const t = useTranslate()
  const failing = contrastChecks(theme).filter((check) => check.ratio < WCAG_AA_CONTRAST)

  if (failing.length === 0) {
    return (
      <p className="theme-contrast theme-contrast--ok" role="status">
        {t('dashboard.theme.contrast.ok', { threshold: WCAG_AA_CONTRAST })}
      </p>
    )
  }

  return (
    // `role="status"` en niet `alert`: dit is geen fout maar een waarschuwing, en
    // hij verandert bij elke schuif van de kleurkiezer. Een alert zou een
    // schermlezer bij iedere tint onderbreken.
    <div className="notice notice--warning theme-contrast" role="status">
      <p className="notice__title">{t('dashboard.theme.contrast.title')}</p>
      <ul className="theme-contrast__list">
        {failing.map((check) => (
          <li key={check.id}>{t(check.messageKey, { ratio: roundRatio(check.ratio) })}</li>
        ))}
      </ul>
      <p className="theme-contrast__hint">
        {t('dashboard.theme.contrast.hint', { threshold: WCAG_AA_CONTRAST })}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Voorbeeldweergave
// ---------------------------------------------------------------------------

/**
 * CSS-variabelen naast de gewone stijl-eigenschappen. React kent aangepaste
 * eigenschappen wel, maar `CSSProperties` beschrijft ze niet; dit type voegt ze
 * toe zonder `any`.
 */
type ThemeVariables = CSSProperties & Record<`--${string}`, string>

/**
 * Een indruk van de gastensite met de gekozen waarden.
 *
 * De kleuren gaan als CSS-variabele mee — dat is de enige weg, want een kleur is
 * een vrije waarde. Alle andere keuzes gaan als `data-`attribuut mee en worden in
 * `index.css` vertaald naar lettertype, letterafstand en hoekstraal. Zo staat er
 * geen enkele opmaakwaarde uit de database in dit bestand: de applicatie kent de
 * betekenis van `editorial`, de database alleen het woord.
 *
 * De knoppen zijn `span`-elementen en geen `button`. Dit is een afbeelding van een
 * website, geen bedieningspaneel; een echte knop zou focus krijgen en niets doen.
 */
function ThemePreview({ theme }: { readonly theme: ResidenceTheme }): ReactElement {
  const t = useTranslate()

  const style: ThemeVariables = {
    '--theme-preview-primary': theme.primaryColor,
    '--theme-preview-secondary': theme.secondaryColor,
    '--theme-preview-accent': theme.accentColor,
    '--theme-preview-text': theme.textColor,
    '--theme-preview-background': theme.backgroundColor,
  }

  return (
    <figure className="theme-preview-frame">
      <figcaption className="theme-preview-frame__caption">
        {t('dashboard.theme.preview.title')}
      </figcaption>

      <div
        className="theme-preview"
        data-button={theme.buttonStyle}
        data-corner={theme.cornerStyle}
        data-font={theme.fontFamily}
        data-heading={theme.headingStyle}
        data-layout={theme.layout}
        style={style}
      >
        {theme.logoUrl !== null && (
          <img
            alt={t('dashboard.theme.preview.logoAlt')}
            className="theme-preview__logo"
            src={mediaFileUrl(theme.logoUrl)}
          />
        )}

        <p className="theme-preview__eyebrow">{t('dashboard.theme.preview.eyebrow')}</p>
        <h3 className="theme-preview__headline">{t('dashboard.theme.preview.headline')}</h3>
        <p className="theme-preview__body">{t('dashboard.theme.preview.body')}</p>

        <div className="theme-preview__actions">
          <span className="theme-preview__button theme-preview__button--primary">
            {t('dashboard.theme.preview.action')}
          </span>
          <span className="theme-preview__button theme-preview__button--ghost">
            {t('dashboard.theme.preview.secondaryAction')}
          </span>
        </div>

        <div className="theme-preview__card">
          <p className="theme-preview__cardTitle">{t('dashboard.theme.preview.cardTitle')}</p>
          <p className="theme-preview__cardBody">{t('dashboard.theme.preview.cardBody')}</p>
        </div>

        {/* Het blokje met de kleuren: vijf vlakken naast elkaar, zodat ook de
            accentkleur zichtbaar is als de rest hem nergens gebruikt. */}
        <ul className="theme-preview__swatches">
          {THEME_COLOR_FIELDS.map((field) => (
            <li className="theme-preview__swatch" key={field}>
              <span
                aria-hidden="true"
                className="theme-preview__swatchChip"
                style={{ background: theme[field] }}
              />
              <span className="theme-preview__swatchName">{t(COLOR_LABEL_KEYS[field])}</span>
              {/* De hexwaarde is data en wordt niet vertaald. */}
              <code className="theme-preview__swatchHex">{theme[field]}</code>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  )
}

// ---------------------------------------------------------------------------
// Velden
// ---------------------------------------------------------------------------

interface ColorFieldProps {
  readonly field: ThemeColorField
  /** De geldige kleur; stuurt de kleurkiezer aan. */
  readonly value: string
  /** Wat er letterlijk in het tekstveld staat; mag tijdelijk ongeldig zijn. */
  readonly text: string
  readonly disabled: boolean
  readonly onChange: (field: ThemeColorField, value: string) => void
}

/**
 * Eén kleur: de systeemkiezer en een hexveld ernaast.
 *
 * Het hexveld is er omdat een merkrichtlijn een hexcode noemt en niet een plek in
 * een kleurenwaaier. Het accepteert `#rrggbb`, `rrggbb` en de korte vorm `#rgb`;
 * zolang de tekst geen kleur is, blijft de laatst geldige waarde staan en meldt
 * het veld wat eraan schort.
 */
function ColorField({ field, value, text, disabled, onChange }: ColorFieldProps): ReactElement {
  const t = useTranslate()
  const inputId = `theme-color-${field}`
  const problemId = `${inputId}-problem`
  const label = t(COLOR_LABEL_KEYS[field])
  const valid = normalizeHexColor(text) !== null

  return (
    <div className="theme-color">
      <label className="theme-color__label" htmlFor={inputId}>
        {label}
      </label>
      <div className="theme-color__controls">
        <input
          className="theme-color__picker"
          disabled={disabled}
          id={inputId}
          onChange={(event) => {
            onChange(field, event.target.value)
          }}
          type="color"
          value={value}
        />
        <input
          aria-describedby={valid ? undefined : problemId}
          aria-invalid={!valid}
          aria-label={t('dashboard.theme.color.hexFor', { color: label })}
          autoComplete="off"
          className="field__input theme-color__hex"
          disabled={disabled}
          onChange={(event) => {
            onChange(field, event.target.value)
          }}
          spellCheck={false}
          type="text"
          value={text}
        />
      </div>
      {!valid && (
        <p className="theme-color__problem" id={problemId}>
          {t('dashboard.theme.color.invalid')}
        </p>
      )}
    </div>
  )
}

interface ChoiceFieldProps<T extends string> {
  readonly id: string
  readonly labelKey: string
  readonly value: T
  readonly options: readonly T[]
  readonly labels: Readonly<Record<T, string>>
  readonly disabled: boolean
  readonly onChange: (value: T) => void
}

/** Eén keuzelijst. De opties komen uit de CHECK-constraint van migratie 0010. */
function ChoiceField<T extends string>({
  id,
  labelKey,
  value,
  options,
  labels,
  disabled,
  onChange,
}: ChoiceFieldProps<T>): ReactElement {
  const t = useTranslate()
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {t(labelKey)}
      </label>
      <select
        className="field__input"
        disabled={disabled}
        id={id}
        onChange={(event) => {
          onChange(pickOption(event.target.value, options, value))
        }}
        value={value}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {t(labels[option])}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Vertaalde melding plus het onvertaalde technische detail ernaast. */
function FailureNotice({ error }: { readonly error: ThemeFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(themeErrorMessageKey(error))}</p>
      {/* Diagnose-informatie, bewust onvertaald — zelfde keuze als op de mediapagina. */}
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Het scherm
// ---------------------------------------------------------------------------

export function ThemePage(): ReactElement {
  const t = useTranslate()

  const [residencesState, setResidencesState] = useState<ResidencesState>({ phase: 'loading' })
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [themeState, setThemeState] = useState<ThemeState>({ phase: 'idle' })
  const [editor, setEditor] = useState<Editor | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<ThemeFailure | null>(null)
  const [savedNotice, setSavedNotice] = useState(false)

  const residencesControllerRef = useRef<AbortController | null>(null)
  const themeControllerRef = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    residencesControllerRef.current?.abort()
    const controller = new AbortController()
    residencesControllerRef.current = controller

    const result = await listResidences({ signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    if (!result.ok) {
      setResidencesState({ phase: 'error', error: result.error })
      return
    }
    setResidencesState({ phase: 'ready', residences: result.data })
    // De eerste residence volstaat als beginkeuze; wie er meer heeft, kiest zelf.
    const first = result.data[0]
    setResidenceId(first === undefined ? null : first.id)
  }, [])

  const loadTheme = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      setThemeState({ phase: 'idle' })
      setEditor(null)
      return
    }
    // Een lopende aanvraag afbreken voorkomt dat een traag antwoord een nieuwer
    // antwoord overschrijft.
    themeControllerRef.current?.abort()
    const controller = new AbortController()
    themeControllerRef.current = controller

    const result = await fetchTheme(residenceId, { signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    setSavedNotice(false)
    setActionError(null)
    if (!result.ok) {
      setThemeState({ phase: 'error', error: result.error })
      setEditor(null)
      return
    }
    setThemeState({ phase: 'ready', server: result.data })
    setEditor(toEditor(result.data))
  }, [residenceId])

  useEffect(() => {
    // Zie de toelichting op hetzelfde patroon in media-page.tsx: state wordt pas
    // ná de await gezet, de begintoestand is al 'loading', en de cleanup breekt de
    // aanvraag af. De lintregel kan dat statisch niet zien.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
    return () => {
      residencesControllerRef.current?.abort()
    }
  }, [loadResidences])

  useEffect(() => {
    if (residenceId === null) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeState({ phase: 'loading' })
    void loadTheme()
    return () => {
      themeControllerRef.current?.abort()
    }
  }, [residenceId, loadTheme])

  /**
   * Neemt een kleur over. De tekst gaat er altijd in — anders is er niet te
   * typen — maar de huisstijl zelf verandert pas zodra er een kleur van te maken
   * is. Zo blijft de belofte staan dat `editor.theme` uitsluitend geldige
   * kleuren bevat.
   */
  const handleColorChange = useCallback((field: ThemeColorField, raw: string): void => {
    setSavedNotice(false)
    setEditor((current) => {
      if (current === null) {
        return current
      }
      const normalized = normalizeHexColor(raw)
      return {
        theme: normalized === null ? current.theme : { ...current.theme, [field]: normalized },
        colorText: { ...current.colorText, [field]: raw },
      }
    })
  }, [])

  /** Eén van de vijf keuzes; het type van de aanroeper bewaakt welke waarde mag. */
  const handleChoiceChange = useCallback(
    (update: Partial<Pick<ResidenceTheme, ThemeChoiceField>>): void => {
      setSavedNotice(false)
      setEditor((current) =>
        current === null ? current : { ...current, theme: { ...current.theme, ...update } },
      )
    },
    [],
  )

  const handleReset = useCallback((): void => {
    if (themeState.phase !== 'ready') {
      return
    }
    setSavedNotice(false)
    setActionError(null)
    setEditor(toEditor(themeState.server))
  }, [themeState])

  const handleSave = useCallback(async (): Promise<void> => {
    if (residenceId === null || editor === null || themeState.phase !== 'ready') {
      return
    }
    const patch = diffTheme(themeState.server, editor.theme)
    if (isEmptyPatch(patch)) {
      return
    }

    setBusy(true)
    setActionError(null)
    setSavedNotice(false)

    const result = await updateTheme(residenceId, patch)
    if (!result.ok) {
      setActionError(result.error)
      setBusy(false)
      return
    }
    // Het antwoord is de bron, niet de bewerking: de API mag normaliseren en een
    // ander veld hebben bijgewerkt dan hier is aangeboden.
    setThemeState({ phase: 'ready', server: result.data })
    setEditor(toEditor(result.data))
    setSavedNotice(true)
    setBusy(false)
  }, [editor, residenceId, themeState])

  const dirty =
    themeState.phase === 'ready' &&
    editor !== null &&
    !isEmptyPatch(diffTheme(themeState.server, editor.theme))

  return (
    <section aria-labelledby="theme-heading" className="page">
      <div className="page__header">
        <h1 className="page__title" id="theme-heading">
          {t('dashboard.theme.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || themeState.phase === 'loading' || busy}
          onClick={() => {
            setThemeState({ phase: 'loading' })
            void loadTheme()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      <p className="page__summary">{t('dashboard.theme.intro')}</p>

      {residencesState.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}

      {residencesState.phase === 'error' && <FailureNotice error={residencesState.error} />}

      {residencesState.phase === 'ready' && residencesState.residences.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.theme.noResidence')}
        </p>
      )}

      {/* De keuzelijst verschijnt pas bij meer dan één residence; bij precies één
          is er niets te kiezen en zou hij alleen ruis zijn. */}
      {residencesState.phase === 'ready' && residencesState.residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.theme.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residencesState.residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {/* Naam uit de database: data, geen interfacetekst. */}
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {themeState.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}

      {themeState.phase === 'error' && <FailureNotice error={themeState.error} />}

      {editor !== null && (
        <form
          className="theme-editor"
          onSubmit={(event) => {
            event.preventDefault()
            void handleSave()
          }}
        >
          <div className="theme-editor__fields">
            <fieldset className="theme-group">
              <legend className="theme-group__legend">{t('dashboard.theme.colors.title')}</legend>
              <p className="theme-group__hint">{t('dashboard.theme.colors.hint')}</p>
              {THEME_COLOR_FIELDS.map((field) => (
                <ColorField
                  disabled={busy}
                  field={field}
                  key={field}
                  onChange={handleColorChange}
                  text={editor.colorText[field]}
                  value={editor.theme[field]}
                />
              ))}
            </fieldset>

            <fieldset className="theme-group">
              <legend className="theme-group__legend">
                {t('dashboard.theme.typography.title')}
              </legend>
              <p className="theme-group__hint">{t('dashboard.theme.typography.hint')}</p>
              <ChoiceField
                disabled={busy}
                id="theme-font-family"
                labelKey="dashboard.theme.fontFamily.label"
                labels={FONT_FAMILY_LABEL_KEYS}
                onChange={(fontFamily) => {
                  handleChoiceChange({ fontFamily })
                }}
                options={THEME_FONT_FAMILIES}
                value={editor.theme.fontFamily}
              />
              <ChoiceField
                disabled={busy}
                id="theme-heading-style"
                labelKey="dashboard.theme.headingStyle.label"
                labels={HEADING_STYLE_LABEL_KEYS}
                onChange={(headingStyle) => {
                  handleChoiceChange({ headingStyle })
                }}
                options={THEME_HEADING_STYLES}
                value={editor.theme.headingStyle}
              />
            </fieldset>

            <fieldset className="theme-group">
              <legend className="theme-group__legend">{t('dashboard.theme.shape.title')}</legend>
              <p className="theme-group__hint">{t('dashboard.theme.shape.hint')}</p>
              <ChoiceField
                disabled={busy}
                id="theme-button-style"
                labelKey="dashboard.theme.buttonStyle.label"
                labels={BUTTON_STYLE_LABEL_KEYS}
                onChange={(buttonStyle) => {
                  handleChoiceChange({ buttonStyle })
                }}
                options={THEME_BUTTON_STYLES}
                value={editor.theme.buttonStyle}
              />
              <ChoiceField
                disabled={busy}
                id="theme-corner-style"
                labelKey="dashboard.theme.cornerStyle.label"
                labels={CORNER_STYLE_LABEL_KEYS}
                onChange={(cornerStyle) => {
                  handleChoiceChange({ cornerStyle })
                }}
                options={THEME_CORNER_STYLES}
                value={editor.theme.cornerStyle}
              />
              <ChoiceField
                disabled={busy}
                id="theme-layout"
                labelKey="dashboard.theme.layout.label"
                labels={LAYOUT_LABEL_KEYS}
                onChange={(layout) => {
                  handleChoiceChange({ layout })
                }}
                options={THEME_LAYOUTS}
                value={editor.theme.layout}
              />
            </fieldset>
          </div>

          <div className="theme-editor__preview">
            <ContrastNotice theme={editor.theme} />
            <ThemePreview theme={editor.theme} />
          </div>

          <div className="theme-editor__actions">
            <button className="button button--primary" disabled={!dirty || busy} type="submit">
              {busy ? t('dashboard.theme.saving') : t('dashboard.theme.save')}
            </button>
            <button
              className="button"
              disabled={!dirty || busy}
              onClick={handleReset}
              type="button"
            >
              {t('dashboard.theme.undo')}
            </button>
            {dirty && <span className="theme-editor__state">{t('dashboard.theme.unsaved')}</span>}
            {!dirty && savedNotice && (
              <span className="theme-editor__state" role="status">
                {t('dashboard.theme.saved')}
              </span>
            )}
          </div>

          {actionError !== null && <FailureNotice error={actionError} />}
        </form>
      )}
    </section>
  )
}
